// Service EmailJS corrigé pour résoudre l'erreur 422
// Configuration simplifiée et robuste

const EMAILJS_CONFIG = {
    serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_kra08rq',
    templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_v66saeg',
    publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'vBGVQPlH-8YUn_pyp',
    privateKey: process.env.REACT_APP_EMAILJS_PRIVATE_KEY || 'oPMrJu4VRWMpoOkzJ5ItW'
};

export const emailServiceCDN = {
    _initialized: false,

    // Vérifier si EmailJS est disponible
    isAvailable() {
        return typeof window !== 'undefined' && window.emailjs;
    },

    // Initialiser EmailJS
    init() {
        if (this.isAvailable()) {
            try {
                window.emailjs.init(EMAILJS_CONFIG.publicKey);
                console.log('✅ EmailJS initialisé avec succès');
                this._initialized = true;
                return true;
            } catch (error) {
                console.error('❌ Erreur lors de l\'initialisation d\'EmailJS:', error);
                this._initialized = false;
                return false;
            }
        }
        console.error('❌ EmailJS non disponible. Vérifiez que le CDN est chargé.');
        this._initialized = false;
        return false;
    },

    // Envoyer un email avec gestion intelligente de l'erreur 422
    async sendNotificationEmail(formData) {
        if (!this.isAvailable()) {
            console.error('EmailJS non disponible');
            return {
                success: false,
                message: 'EmailJS non disponible'
            };
        }

        try {
            // S'assurer qu'EmailJS est initialisé
            if (!this._initialized) {
                this.init();
            }

            console.log('📧 Tentative d\'envoi d\'email...');

            // Normaliser les champs additionnels
            const newsletterFlag = (formData.newsletter === true || String(formData.newsletter).toLowerCase() === 'true') ? 'Oui' : 'Non';
            const phoneVal = formData.telephone || formData.phone || '';
            const subjectVal = formData.sujet || formData.subject || 'Contact depuis le site';

            // Essayer différents formats de données pour contourner l'erreur 422
            const formats = [
                // Format 1: Ultra-simple (recommandé pour déboguer)
                {
                    name: 'Simple',
                    params: {
                        name: formData.nom,
                        email: formData.email,
                        message: formData.message,
                        // Ajouts
                        telephone: phoneVal,
                        phone: phoneVal,
                        sujet: subjectVal,
                        subject: subjectVal,
                        newsletter: newsletterFlag
                    }
                },
                // Format 2: Variables user_* 
                {
                    name: 'User prefix',
                    params: {
                        user_name: formData.nom,
                        user_email: formData.email,
                        user_message: formData.message,
                        // Ajouts
                        telephone: phoneVal,
                        phone: phoneVal,
                        sujet: subjectVal,
                        subject: subjectVal,
                        newsletter: newsletterFlag
                    }
                },
                // Format 3: Variables from_*
                {
                    name: 'From prefix',
                    params: {
                        from_name: formData.nom,
                        from_email: formData.email,
                        message: formData.message,
                        // Ajouts
                        telephone: phoneVal,
                        phone: phoneVal,
                        sujet: subjectVal,
                        subject: subjectVal,
                        newsletter: newsletterFlag
                    }
                },
                // Format 4: Template EmailJS standard
                {
                    name: 'Standard EmailJS',
                    params: {
                        to_name: 'New School',
                        from_name: formData.nom,
                        reply_to: formData.email,
                        message: formData.message,
                        subject: subjectVal,
                        // Ajouts
                        telephone: phoneVal,
                        phone: phoneVal,
                        sujet: subjectVal,
                        newsletter: newsletterFlag
                    }
                }
            ];

            // Tester chaque format jusqu'à ce qu'un fonctionne
            for (const format of formats) {
                try {
                    console.log(`🧪 Test format "${format.name}":`, format.params);

                    const result = await window.emailjs.send(
                        EMAILJS_CONFIG.serviceId,
                        EMAILJS_CONFIG.templateId,
                        format.params
                    );

                    console.log(`✅ SUCCÈS avec format "${format.name}"!`, result);

                    return {
                        success: true,
                        message: `Email envoyé avec succès (format: ${format.name})`,
                        format: format.name,
                        emailResult: result
                    };

                } catch (error) {
                    console.warn(`❌ Format "${format.name}" échoué:`, error.status, error.text);

                    // Si ce n'est pas la dernière tentative, continuer
                    if (format !== formats[formats.length - 1]) {
                        continue;
                    }

                    // Si c'est la dernière tentative, lancer l'erreur
                    throw error;
                }
            }

        } catch (error) {
            console.error('❌ Toutes les tentatives ont échoué:', error);

            return {
                success: false,
                message: this._getErrorMessage(error),
                error: error
            };
        }
    },

    // Obtenir un message d'erreur plus informatif
    _getErrorMessage(error) {
        if (error.status === 422) {
            return `Erreur 422: Configuration EmailJS incorrecte. 
            Vérifiez que votre template EmailJS utilise les bonnes variables.
            Variables testées: name, email, message, user_name, user_email, from_name, etc.`;
        }

        if (error.status === 401) {
            return 'Erreur 401: Clé publique EmailJS invalide';
        }

        if (error.status === 404) {
            return 'Erreur 404: Service ID ou Template ID introuvable';
        }

        return 'Erreur lors de l\'envoi: ' + (error.text || error.message || 'Erreur inconnue');
    },

    // Test de connexion
    async testConnection() {
        if (!this.isAvailable()) {
            console.log('❌ EmailJS non disponible via CDN');
            return false;
        }

        try {
            if (!this._initialized) {
                this.init();
            }
            console.log('✅ EmailJS configuré et prêt');
            return true;
        } catch (error) {
            console.error('❌ Erreur lors de l\'initialisation d\'EmailJS:', error);
            return false;
        }
    },

    // Diagnostic complet
    async diagnose() {
        console.log('🔍 === DIAGNOSTIC EMAILJS ===');

        // Test 1: Disponibilité
        const available = this.isAvailable();
        console.log('📋 EmailJS disponible:', available);

        if (!available) {
            console.log('❌ EmailJS CDN non chargé. Vérifiez index.html');
            return false;
        }

        // Test 2: Configuration
        console.log('📋 Configuration:');
        console.log('   - Service ID:', EMAILJS_CONFIG.serviceId);
        console.log('   - Template ID:', EMAILJS_CONFIG.templateId);
        console.log('   - Public Key:', EMAILJS_CONFIG.publicKey.substring(0, 8) + '...');

        // Test 3: Initialisation
        try {
            const initResult = this.init();
            console.log('📋 Initialisation:', initResult ? '✅ OK' : '❌ ÉCHEC');
        } catch (error) {
            console.log('❌ Erreur d\'initialisation:', error);
            return false;
        }

        // Test 4: Test d'envoi avec données minimales
        const testData = {
            nom: 'Test Diagnostic',
            email: 'test@example.com',
            message: 'Test automatique de diagnostic EmailJS'
        };

        console.log('📋 Test d\'envoi avec données minimales...');
        try {
            const result = await this.sendNotificationEmail(testData);
            console.log('📋 Résultat du test:', result);
            return result.success;
        } catch (error) {
            console.log('❌ Erreur lors du test:', error);
            return false;
        }
    }
};
